import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MapPin, Clock, Pill, Navigation, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import DocumentationModal from "@/components/DocumentationModal";

export default function ClockIn() {
  const [shifts, setShifts] = useState([]);
  const [pos, setPos] = useState(null);
  const [gps, setGps] = useState("idle");
  const [busy, setBusy] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [pendingShift, setPendingShift] = useState(null);

  const load = () => api.get("/shifts", { params: { limit: 500 } }).then((r)=>setShifts(r.data.data));
  useEffect(() => { load(); }, []);

  const requestGps = () => {
    setGps("locating");
    if (!navigator.geolocation) { setGps("unsupported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat:p.coords.latitude, lng:p.coords.longitude }); setGps("ok"); },
      (e) => { setGps("error"); toast.error("Location denied: " + e.message); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const simulateSiteGps = (shift) => {
    if (shift?.location) {
      setPos({ lat: shift.location.latitude, lng: shift.location.longitude });
      setGps("simulated");
    }
  };

  const doClockIn = async (shift) => {
    if (!pos) { toast.error("Capture your location first"); return; }
    setBusy(shift.id);
    try {
      await api.post("/timesheets/clock-in", { shift_id: shift.id, latitude: pos.lat, longitude: pos.lng });
      toast.success("Clocked in!");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  // Blocking documentation: clock-out is gated behind the documentation
  // modal. The actual POST to /timesheets/clock-out happens INSIDE the
  // modal submit handler so we can submit hours + docs in one round-trip.
  const startClockOut = (shift) => {
    if (!pos) { toast.error("Capture your location first"); return; }
    setPendingShift(shift);
    setDocOpen(true);
  };

  const submitClockOutWithDocs = async ({ service_provided, service_note }) => {
    if (!pendingShift || !pos) throw new Error("Missing shift or location");
    setBusy(pendingShift.id);
    try {
      const { data } = await api.post("/timesheets/clock-out", {
        shift_id: pendingShift.id,
        latitude: pos.lat,
        longitude: pos.lng,
        service_provided,
        service_note,
      });
      toast.success(`Clocked out — ${data.hours} hrs · documentation saved`);
      load();
      setPendingShift(null);
    } finally {
      setBusy(null);
    }
  };

  const now = new Date();
  const activeShift = shifts.find((s) => {
    const start = new Date(s.start_time), end = new Date(s.end_time);
    return start <= now && now <= end && s.status !== "completed";
  }) || shifts.find((s) => new Date(s.end_time) > now);

  const inProgress = shifts.find((s) => s.status === "in_progress");
  const primaryShift = inProgress || activeShift;
  const isInProgress = !!inProgress;

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <div className="accp-label">Workforce</div>
        <h1 className="text-3xl md:text-4xl font-semibold text-primary" style={{fontFamily:'Outfit'}}>Clock in / out</h1>
        <p className="text-muted-foreground mt-1 text-sm">200 ft GPS geofence enforced.</p>
      </div>

      {/* GPS card */}
      <div className={`accp-card p-5 ${pos ? "" : "border-l-4 border-l-[#E2B400]"}`}>
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${pos ? "bg-accent text-primary" : "bg-[#FFF8E6] text-[#7A5A00]"}`}>
            {pos ? <Navigation size={20}/> : <AlertTriangle size={20}/>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="accp-label">Your location</div>
            <div className="font-semibold text-primary mt-0.5 font-mono text-sm truncate">
              {pos ? `${pos.lat.toFixed(5)}, ${pos.lng.toFixed(5)}` : "Not captured"}
              {gps==="simulated" && <div className="text-[10px] opacity-60 font-sans">(simulated at site)</div>}
            </div>
          </div>
        </div>
        <button onClick={requestGps} className="accp-btn-outline w-full mt-4 py-3" data-testid="capture-gps-btn">
          {gps==="locating" ? <><Loader2 size={16} className="animate-spin"/> Locating…</> : <><Navigation size={16}/> Capture GPS</>}
        </button>
        {primaryShift?.location && (
          <button onClick={()=>simulateSiteGps(primaryShift)} className="accp-btn-ghost text-xs w-full mt-2" data-testid="sim-gps-btn">
            Simulate presence at {primaryShift.location.name}
          </button>
        )}
      </div>

      {/* Primary shift CTA — sticky on mobile */}
      {primaryShift && (
        <div className="accp-card p-5 ring-2 ring-primary" data-testid={`clock-shift-${primaryShift.id}`}>
          <div className="flex items-start justify-between mb-3 gap-3">
            <div className="min-w-0">
              <div className="accp-label font-mono">{primaryShift.code} · {primaryShift.status}</div>
              <h3 className="text-xl font-semibold text-primary truncate" style={{fontFamily:'Outfit'}}>{primaryShift.location?.name}</h3>
            </div>
            {primaryShift.requires_medication && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full compliance-expired text-[11px] font-semibold shrink-0">
                <Pill size={11}/> MEDS
              </span>
            )}
          </div>
          <div className="space-y-1.5 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Clock size={13}/> {new Date(primaryShift.start_time).toLocaleString()} → {new Date(primaryShift.end_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</div>
            <div className="flex items-center gap-2"><MapPin size={13}/> {primaryShift.location?.address}</div>
          </div>

          {/* Massive thumb-zone CTA */}
          <div className="mt-5">
            {isInProgress ? (
              <button
                className="accp-btn-primary w-full py-5 text-lg font-semibold"
                onClick={()=>startClockOut(primaryShift)} disabled={busy===primaryShift.id}
                data-testid={`clock-out-btn-${primaryShift.id}`}
              >
                {busy===primaryShift.id ? <Loader2 size={20} className="animate-spin"/> : <Clock size={20}/>}
                Clock out
              </button>
            ) : (
              <button
                className="accp-btn-primary w-full py-5 text-lg font-semibold"
                onClick={()=>doClockIn(primaryShift)} disabled={busy===primaryShift.id}
                data-testid={`clock-in-btn-${primaryShift.id}`}
              >
                {busy===primaryShift.id ? <Loader2 size={20} className="animate-spin"/> : <CheckCircle2 size={20}/>}
                Clock in
              </button>
            )}
          </div>
        </div>
      )}

      {/* Other upcoming shifts */}
      {shifts.filter(s => s.id !== primaryShift?.id && new Date(s.end_time) > now).length > 0 && (
        <div>
          <div className="accp-label mb-2">Upcoming</div>
          <div className="space-y-2">
            {shifts.filter(s => s.id !== primaryShift?.id && new Date(s.end_time) > now).map((s) => (
              <div key={s.id} className="accp-card p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-mono text-muted-foreground">{s.code}</div>
                    <div className="font-semibold text-primary text-sm truncate">{s.location?.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{new Date(s.start_time).toLocaleString()}</div>
                  </div>
                  {s.requires_medication && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full compliance-expired text-[10px] font-semibold shrink-0">
                      <Pill size={10}/> MEDS
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {shifts.length === 0 && (
        <div className="accp-card p-8 text-center text-muted-foreground">No scheduled shifts.</div>
      )}

      <DocumentationModal
        open={docOpen}
        onOpenChange={setDocOpen}
        title="Document this visit"
        description="Required to clock out. Select the service you provided and write a brief summary."
        submitLabel="Clock out & save"
        onSubmit={submitClockOutWithDocs}
      />
    </div>
  );
}
